import { useGetExamEventSessionExamineesForTimeExtensionQuery } from '@exam-center/graphql';
import {
  Flex,
  Text,
  type DataGridWithCheckboxProps,
  SearchInput,
  Box,
  type GridRowParams,
  NoRowsOverlay,
  Checkbox,
} from '@shared/components';
import { useMemo, useState } from 'react';
import { useController } from 'react-hook-form';

import {
  type ExamineeColumn,
  ExamineeTimeExtensionDataGrid,
  NoExamineeOverlay,
} from '../../components';

import { mapExamSessionExamineesToExamineeTimeExtensionItems } from './utils';

interface ExamineeTimeExtensionListProps {
  name: string;
  examEventId: string;
  currentSessionId: string;
  now: Date;
}

export default function ExamineeTimeExtensionList(props: Readonly<ExamineeTimeExtensionListProps>) {
  const { currentSessionId, name, examEventId, now } = props;
  const {
    field: { onChange, value },
  } = useController({ name });

  const [shouldFilterExtendableExaminees, setShouldFilterExtendableExaminees] = useState(false);

  const [searchParams, setSearchParams] = useState({
    searchValue: '',
    page: 0,
    pageSize: 10,
  });

  const handleRowClick: DataGridWithCheckboxProps['onRowSelectionModelChange'] = (rows) => {
    onChange(rows);
  };

  const handleSearch = (value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      searchValue: value,
      page: 0,
    }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setSearchParams((prev) => ({ ...prev, page: 0, pageSize }));
  };

  const { data, loading: isExamEventSessionExamineesLoading } =
    useGetExamEventSessionExamineesForTimeExtensionQuery({
      variables: {
        getExamEventSessionExamineesForTimeExtensionInput: {
          examEventId,
          sessionId: currentSessionId,
          searchExaminee: {
            filter: {
              searchText: {
                fields: ['fullName'],
                value: searchParams.searchValue,
              },
            },
          },
          paging: {
            currentPage: searchParams.page,
            pageSize: searchParams.pageSize,
          },
          extendableOnly: shouldFilterExtendableExaminees,
        },
      },
      fetchPolicy: 'network-only',
      pollInterval: 10000,
    });

  const results = useMemo(
    () => data?.ecGetExamEventSessionExamineesForTimeExtension?.result ?? [],
    [data?.ecGetExamEventSessionExamineesForTimeExtension?.result],
  );

  const totalExaminees = data?.ecGetExamEventSessionExamineesForTimeExtension?.total ?? 0;

  const selectedExamineeIdCount = value?.length ?? 0;

  const examineesInSession = useMemo(
    () =>
      mapExamSessionExamineesToExamineeTimeExtensionItems({
        results,
        sessionId: currentSessionId,
        now,
      }),
    [results, currentSessionId, now],
  );

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(4),
      })}
    >
      <Flex sx={{ alignItems: 'center' }}>
        <Text variant='h4'>เลือกผู้เข้าสอบ ({selectedExamineeIdCount})</Text>
        <Flex sx={(theme) => ({ gap: theme.spacing(4), justifyContent: 'flex-end', flexGrow: 1 })}>
          <Checkbox
            checked={shouldFilterExtendableExaminees}
            label='แสดงเฉพาะผู้ที่สามารถทดเวลาได้'
            onChange={(e) => setShouldFilterExtendableExaminees(e.target.checked)}
          />
          <Box sx={{ flexBasis: '300px' }}>
            <SearchInput
              onChange={handleSearch}
              placeholder='ค้นหาผู้เข้าสอบ...'
              debounceTime={700}
            />
          </Box>
        </Flex>
      </Flex>
      <ExamineeTimeExtensionDataGrid
        loading={isExamEventSessionExamineesLoading}
        rows={examineesInSession}
        rowSelectionModel={value}
        onRowSelectionModelChange={handleRowClick}
        paginationModel={{
          page: searchParams.page,
          pageSize: searchParams.pageSize,
        }}
        isRowSelectable={(params: GridRowParams<ExamineeColumn>) => params.row.isExtendable}
        getRowClassName={(params: GridRowParams<ExamineeColumn>) =>
          params.row.isExtendable ? '' : 'non-selectable-row'
        }
        sx={{
          '& .non-selectable-row': {
            pointerEvents: 'none',
            color: (theme) => theme.palette.text.disabled,
          },
          '--DataGrid-overlayHeight': '412px',
        }}
        keepNonExistentRowsSelected
        slots={{
          noRowsOverlay: () =>
            searchParams.searchValue ? <NoRowsOverlay /> : <NoExamineeOverlay />,
        }}
        slotProps={{
          footer: {
            total: totalExaminees,
            pagination: {
              page: searchParams.page,
              count: totalExaminees,
              rowsPerPage: searchParams.pageSize,
              onPageChange: (_event, page) => handlePageChange(page),
              onRowsPerPageChange: (event) => handlePageSizeChange(Number(event.target.value)),
            },
          },
        }}
      />
    </Flex>
  );
}
